import React from 'react';
import moment from 'moment';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Linkify from 'react-linkify';
import DOMPurify from 'dompurify';
import BaseMUIDataTable from '../../../../../components/BaseMUIDataTable/BaseMUIDataTable';
import { Colors } from '../../../../../assets/theme/Colors';
import { formatTime, formatDate, formatCsvFileName } from '../../../../../helpers/formatters';
import { getMuiTableDataIndex } from '../../../../../helpers/getters';
import {
  donorLinkFilters,
  getRescuePickupLocationFullName,
  getRescuePosition,
  isHTML,
  stripHTML,
} from '../../../../../helpers/RescuesHelper';
import ReleaseRescueDesktopButtons from '../../../../../components/RescueNotification/ReleaseRescueDesktopButtons';
import useHasActiveFeatureFlag from '../../../../../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from '../../../../../components/FeatureFlagWrapper';
import DonorLinkPickupIndicator from '../../../../../components/DonorLinkPickupIndicator';
import { sortByDateAndPickupWindowStartAndEndTime, sortByPickupReady } from '../../../../../helpers/sorters';
import { getUserFullName } from '../../../../../helpers/user';
import useActiveUser from '../../../../../hooks/useActiveUser';

const RescuerDashboardMyRescuesTable = ({ tableId, rescues, onCellClick }) => {
  const activeUser = useActiveUser();
  const rescuerName = getUserFullName(activeUser);
  const hasActiveDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);

  const pickupReadyCustomBodyRender = (value, tableMeta, csv = false) => {
    const rescue = rescues[getMuiTableDataIndex(tableMeta)];

    if (!rescue) {
      return null;
    }

    if (!rescue?.location_donor_link_active) {
      return '-';
    }

    if (csv) {
      return value ? 'Pickup Ready' : 'No food yet';
    }

    return <DonorLinkPickupIndicator pickupReady={!!value} />;
  };

  return (
    <ThemeProvider
      theme={(theme) =>
        createTheme({
          ...theme,
          components: {
            ...theme.components,
            MUIDataTableHeadCell: {
              styleOverrides: {
                root: {
                  ...theme.components.MUIDataTableHeadCell.styleOverrides.root,
                  '&:last-child': {
                    width: 255,
                  },
                },
              },
            },
          },
        })
      }
    >
      <BaseMUIDataTable
        tableId={tableId}
        data={rescues}
        columns={[
          {
            name: 'id',
            label: 'ID',
            options: {
              filter: false,
              sort: false,
              display: false,
            },
          },
          {
            name: 'date',
            label: 'Date',
            options: {
              setCellProps: () => ({ sx: { width: 125 } }),
              filterOptions: {
                renderValue: formatDate,
              },
              customFilterListOptions: {
                render: value => `Date: ${formatDate(value)}`,
              },
              sortCompare:
                (order) =>
                ({ rowData: row1 }, { rowData: row2 }) =>
                  sortByDateAndPickupWindowStartAndEndTime({
                    order,
                    firstDate: row1[1],
                    firstBegin: row1[3],
                    firstEnd: row1[4],
                    secondDate: row2[1],
                    secondBegin: row2[3],
                    secondEnd: row2[4],
                  }),
              customBodyRenderCSV: (value) => {
                return moment(value, 'YYYYMMDD').format('MM/DD/YYYY');
              },
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                return (
                  <>
                    <span>
                      {moment(value, 'YYYYMMDD').format('MM/DD/YYYY')}
                    </span>

                    {rescue && rescue.cancelled_by_id && (
                      <>
                        <br />

                        <span style={{ color: Colors.errorText, fontWeight: 'bold' }}>
                          Cancelled
                        </span>
                      </>
                    )}
                  </>
                );
              },
            },
          },
          {
            name: 'day',
            label: 'Day',
            options: {
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRenderCSV: (value, tableMeta) =>
                moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd'),
              customBodyRender: (value, tableMeta) =>
                moment(rescues[getMuiTableDataIndex(tableMeta)].date, 'YYYYMMDD').format('dddd'),
            },
          },
          {
            name: 'pickup_begin',
            label: 'Pickup Begin',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 } }),
              customBodyRenderCSV: formatTime,
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'pickup_end',
            label: 'Pickup End',
            options: {
              filter: false,
              setCellProps: () => ({ sx: { width: 125 }}),
              customBodyRenderCSV: formatTime,
              customBodyRender: value => formatTime(value),
            },
          },
          {
            name: 'location',
            label: 'Food Donor',
            options: {
              customFilterListOptions: {
                render: value => `Food Donor: ${value}`,
              },
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue) {
                  return null;
                }

                return getRescuePickupLocationFullName(rescue);
              },
            },
          },
          {
            name: 'receiver',
            label: 'Receiver',
            options: {
              customBodyRender: value => value || '',
            },
          },
          ...(hasActiveDonorLinkFF
            ? [
                {
                  name: 'rescue_confirmed',
                  label: 'Donor Link',
                  options: {
                    display: true,
                    filter: true,
                    customBodyRender: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta),
                    customBodyRenderCSV: (value, tableMeta) => pickupReadyCustomBodyRender(value, tableMeta, true),
                    filterOptions: {
                      names: [
                        donorLinkFilters.dataReceived,
                        donorLinkFilters.dataNotReceived,
                        donorLinkFilters.donorLinkInactive,
                        donorLinkFilters.donorLinkActive,
                      ],
                      logic: (option, filters, row) => {
                        if (!filters || filters.length === 0) {
                          return false;
                        }

                        const rescueId = row[0];
                        const rescue = rescues.find((r) => r.id === rescueId);

                        switch (filters[0]) {
                          case donorLinkFilters.dataReceived:
                            return !rescue.rescue_confirmed;
                          case donorLinkFilters.dataNotReceived:
                            return !!option;
                          case donorLinkFilters.donorLinkInactive:
                            return rescue.location_donor_link_active;
                          case donorLinkFilters.donorLinkActive:
                            return !rescue.location_donor_link_active;
                          default:
                            return true;
                        }
                      },
                    },
                    customFilterListOptions: {
                      render: (value) => `Donor Link: ${value}`,
                    },
                    sortCompare: (order) => (row1, row2) => {
                      const rescue1 = rescues.find((fd) => fd.id === row1.rowData[0]);
                      const rescue2 = rescues.find((fd) => fd.id === row2.rowData[0]);

                      return sortByPickupReady(order, rescue1, rescue2);
                    },
                  },
                },
              ]
            : []),
          {
            name: 'slug',
            label: 'Description',
            options: {
              filter: false,
            },
          },
          {
            name: 'position',
            label: 'Position',
            options: {
              filter: false,
              customBodyRenderLite: dataIndex => getRescuePosition(rescues[dataIndex]),
            },
          },
          {
            name: 'pickup_notes',
            label: 'Pickup Notes',
            options: {
              customBodyRenderCSV: (value) => {
                return stripHTML(value);
              },
              customBodyRender: (value) => {
                if (isHTML(value)) {
                  return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
                }

                return (
                  <Linkify
                    componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a target="blank" href={decoratedHref} key={key}>
                        {decoratedText}
                      </a>
                    )}
                  >
                    {value}
                  </Linkify>
                );
              },
              filter: false,
            },
          },
          {
            name: 'actions',
            label: 'Actions',
            options: {
              download: false,
              Empty: true,
              filter: false,
              searchable: false,
              viewColumns: false,
              customBodyRender: (value, tableMeta) => {
                const rescue = rescues[getMuiTableDataIndex(tableMeta)];

                if (!rescue || rescue.cancelled_by_id !== null) {
                  return false;
                }

                return <ReleaseRescueDesktopButtons rescue={rescue} />;
              },
            },
          },
        ]}
        options={{
          sortOrder: {
            name: 'date',
            direction: 'asc',
          },
          elevation: 0,
          rowsPerPage: 15,
          selectableRows: 'none',
          responsive: 'simple',
          downloadOptions: {
            filename: formatCsvFileName(`${rescuerName} My Rescues`),
          },
          onCellClick: (colData, cellMeta) => {
            // ignore click on "actions" column cell
            if (cellMeta.colIndex >= 9) {
              return false;
            }

            const rescue = rescues[getMuiTableDataIndex(cellMeta)];

            if (!rescue) {
              return false;
            }

            return onCellClick(rescue);
          },
          setRowProps: (row, dataIndex) => {
            const rescue = rescues[dataIndex];

            if (!rescue || !rescue.cancelled_by_id) {
              return false;
            }

            return {
              sx: {
                '& > td': {
                  opacity: 0.25,
                },
              },
            };
          },
        }}
      />
    </ThemeProvider>
  );
};

export default RescuerDashboardMyRescuesTable;
